import React, { useCallback, useEffect, useMemo, useState } from "react";
import mime from "mime";
import { v4 as uuidv4 } from "uuid";
import { Buffer } from "buffer";
import { FirebaseStorageTypes } from "@react-native-firebase/storage";
import { FirebaseManagerContext, TEST_USER_ID } from "../contexts/FirebaseManager";
import storage from "../contexts/FirebaseStorage";
import { Document, DocumentType, MakeOptional } from "../__generated__/graphql";
import * as ImagePicker from "expo-image-picker";
import * as DocumentPicker from "expo-document-picker";
import { Linking, Platform } from "react-native";
import { DocumentPickerAsset } from "expo-document-picker";
import { ImagePickerAsset } from "expo-image-picker";
import { XOR } from "ts-essentials";
import { useI18n } from "./i18n-hooks";
import { useSnackbar } from "./snackbar";
import { useWebImagePicker } from "../components/webimages/WebImagePicker";
import { useActionSheetOrDropdown } from "./use-action-sheet-or-dropdown";
import { UnifiedImagePickerAsset, useCropZoomImage } from "./crop-zoom-image";

export enum PickerOptionsMode {
    Camera = "camera",
    Library = "library",
    Storage = "storage",
    Web = "webImage",
}

export type UploadDocumentType = MakeOptional<
    Pick<Document, "id" | "documentUrl" | "type" | "title">,
    "documentUrl"
>;

type UseDocumentsUploadType = {
    documentType: DocumentType;
    generateUniqueId?: boolean;
    isPublic?: boolean;
    setIsLoading?: (isLoading: boolean) => void;
    onSelect: (document: UploadDocumentType) => void | Promise<void>;
    modes?: PickerOptionsMode[];
    webUploadFn?: string;
    fixedTitle?: string;
    enableCropZoom?: boolean;
};

export const useDocumentUpload = ({
    documentType,
    generateUniqueId = false,
    isPublic = false,
    setIsLoading,
    onSelect,
    modes = [PickerOptionsMode.Camera, PickerOptionsMode.Library],
    webUploadFn,
    fixedTitle,
    enableCropZoom,
}: UseDocumentsUploadType) => {
    const [cameraStatus, requestCameraPermission] = ImagePicker.useCameraPermissions();
    const [libraryStatus, requestLibraryPermission] = ImagePicker.useMediaLibraryPermissions();
    const { uploadDocument, controller } = useUploadDocument();
    const { showSnackbar } = useSnackbar();
    const i18n = useI18n();
    const pickWebImage = useWebImagePicker();
    const performCropZoom = useCropZoomImage();

    const filteredModes = useMemo(
        () => modes.filter((m) => Platform.OS !== "web" || m !== PickerOptionsMode.Web),
        [modes],
    );

    const ensureCameraPermission = React.useCallback(async () => {
        let granted = true;
        if (!cameraStatus?.granted) {
            ({ granted } = await requestCameraPermission());
        }
        return granted;
    }, [cameraStatus, requestCameraPermission]);

    const cropZoomImage = useCallback(
        async (asset: UnifiedImagePickerAsset) => {
            if (enableCropZoom) {
                return await performCropZoom(asset);
            }
            return asset;
        },
        [enableCropZoom, performCropZoom],
    );

    const ensureLibraryPermission = React.useCallback(async () => {
        let granted = true;
        if (!libraryStatus?.granted) {
            ({ granted } = await requestLibraryPermission());
        }
        return granted;
    }, [libraryStatus, requestLibraryPermission]);

    const prepareUploadDocument = useCallback(
        async (asset: UnifiedImagePickerAsset) => {
            const newDocumentId = uuidv4();

            const baseDocument = {
                title: fixedTitle || asset.name || asset.fileName || "",
                id: newDocumentId,
                type: documentType,
            };

            await onSelect(baseDocument);

            const uploadedDocumentUrl = await uploadDocument(
                asset,
                documentType,
                isPublic,
                generateUniqueId ? newDocumentId : undefined,
            );
            if (uploadedDocumentUrl) {
                await onSelect({
                    ...baseDocument,
                    documentUrl: uploadedDocumentUrl,
                });
            } else {
                throw new Error("Error uploading document");
            }
        },
        [documentType, isPublic, onSelect, uploadDocument, generateUniqueId, fixedTitle],
    );

    const onError = useCallback(async () => {
        showSnackbar({
            text: i18n.t("permission_denied"),
            mode: "error",
        });
        await Linking.openSettings();
    }, [i18n, showSnackbar]);

    const onCamera = useCallback(async () => {
        if (await ensureCameraPermission()) {
            const pickerResult = await ImagePicker.launchCameraAsync({
                mediaTypes: "images" as ImagePicker.MediaType,
                quality: 0.6,
            });
            if (pickerResult?.assets) {
                const newAsset = await cropZoomImage(pickerResult.assets[0]);
                if (newAsset) {
                    await prepareUploadDocument(newAsset);
                }
            }
        } else {
            await onError();
        }
    }, [cropZoomImage, ensureCameraPermission, onError, prepareUploadDocument]);

    const onLibrary = useCallback(async () => {
        if (await ensureLibraryPermission()) {
            const pickerResult = await ImagePicker.launchImageLibraryAsync({
                mediaTypes: "images" as ImagePicker.MediaType,
            });
            if (pickerResult?.assets) {
                const newAsset = await cropZoomImage(pickerResult.assets[0]);
                if (newAsset) {
                    await prepareUploadDocument(newAsset);
                }
            }
        } else {
            await onError();
        }
    }, [cropZoomImage, ensureLibraryPermission, onError, prepareUploadDocument]);

    const onStorage = useCallback(async () => {
        const pickerResult = await DocumentPicker.getDocumentAsync({
            type: ["application/pdf", "images/*"],
        });
        if (pickerResult?.assets) {
            const newAsset = await cropZoomImage(pickerResult.assets[0]);
            if (newAsset) {
                await prepareUploadDocument(newAsset);
            }
        }
    }, [cropZoomImage, prepareUploadDocument]);

    const onWeb = useCallback(async () => {
        const pickerResult = await pickWebImage();

        if (pickerResult?.assets) {
            const newAsset = await cropZoomImage(pickerResult.assets[0]);
            if (newAsset) {
                await prepareUploadDocument(newAsset);
            }
        }
    }, [cropZoomImage, pickWebImage, prepareUploadDocument]);

    const onSelectOption = useCallback(
        async (selectedIndex = 0) => {
            try {
                switch (filteredModes[selectedIndex]) {
                    case PickerOptionsMode.Camera:
                        await onCamera();
                        break;
                    case PickerOptionsMode.Library:
                        await onLibrary();
                        break;
                    case PickerOptionsMode.Storage:
                        await onStorage();
                        break;
                    case PickerOptionsMode.Web:
                        await onWeb();
                        break;
                }
            } catch (e) {
                console.warn(e);
                showSnackbar({
                    text: i18n.t("documents.select_error"),
                    mode: "error",
                });
            } finally {
                setIsLoading?.(false);
            }
        },
        [filteredModes, i18n, onCamera, onLibrary, onStorage, onWeb, setIsLoading, showSnackbar],
    );

    const dropdownItems = useMemo(
        () =>
            filteredModes.map((v, index) => ({
                label: i18n.t(v),
                onClick: () => onSelectOption(index),
                icon:
                    v === PickerOptionsMode.Camera
                        ? ("camera" as const)
                        : v === PickerOptionsMode.Library
                          ? ("images" as const)
                          : v === PickerOptionsMode.Storage
                            ? ("folder" as const)
                            : v === PickerOptionsMode.Web
                              ? ("globe" as const)
                              : undefined,
            })),
        [filteredModes, i18n, onSelectOption],
    );

    const documentDropdownProps = useActionSheetOrDropdown({
        items: dropdownItems,
        position: "right",
        setIsLoading,
    });

    useEffect(() => {
        if (Platform.OS === "web" && webUploadFn) {
            const fnName = `${webUploadFn}Upload`;
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            window[fnName] = prepareUploadDocument;

            return () => {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                window[fnName] = null;
            };
        }
    }, [prepareUploadDocument, webUploadFn]);

    return { documentDropdownProps, controller };
};

export function useUploadDocument() {
    const { user: firebaseUser } = React.useContext(FirebaseManagerContext);
    const [uploadDocumentController, setUploadDocumentController] = useState<AbortController>();

    const uploadDocument = React.useCallback(
        async (
            asset: Pick<XOR<DocumentPickerAsset, ImagePickerAsset>, "mimeType" | "uri">,
            documentType: DocumentType,
            isPublic?: boolean,
            prefix?: string,
        ) => {
            if (firebaseUser) {
                const mimeType = asset.mimeType || "image/png";
                const ext = mime.getExtension(mimeType);
                let filePath;
                if (prefix) {
                    filePath = `/users/${firebaseUser.uid}/${prefix}_${documentType}.${ext}`;
                } else {
                    filePath = `/users/${firebaseUser.uid}/${documentType}.${ext}`;
                }
                const abortController = new AbortController();
                setUploadDocumentController(abortController);
                return uploadDocumentAsync(
                    asset.uri,
                    mimeType,
                    filePath,
                    firebaseUser.uid,
                    abortController.signal,
                    isPublic,
                );
            } else {
                throw new Error("No firebase user id");
            }
        },
        [firebaseUser],
    );

    return { uploadDocument, controller: uploadDocumentController };
}

export const getDocumentUrl = async (filePath: string) => {
    if (!filePath) {
        return null;
    }
    if (filePath.startsWith("http")) {
        return filePath;
    }
    const fileRef = storage().ref(filePath);
    try {
        return await fileRef.getDownloadURL();
    } catch (e) {
        // eslint-disable-next-line no-console
        console.info(e);
        // exception just mean file does not exist we can just return null
        return null;
    }
};

const uploadDocumentAsync = async (
    sourceUri: string,
    mimeType: string,
    destinationFilePath: string,
    firebaseUserId: string,
    uploadDocumentSignal: AbortSignal,
    isPublic?: boolean,
) => {
    const fileRef = storage().ref(destinationFilePath);
    let metadata: FirebaseStorageTypes.SettableMetadata = {
        contentType: mimeType,
    };

    if (!firebaseUserId) {
        throw new Error("No firebase user id");
    }
    if (firebaseUserId === TEST_USER_ID) {
        return `https://__testing__.com/${destinationFilePath}`;
    }
    if (isPublic) {
        metadata = {
            ...metadata,
            customMetadata: {
                owner: "public",
            },
            cacheControl: "public,max-age: 3600",
        };
    } else {
        metadata = {
            ...metadata,
            customMetadata: {
                owner: firebaseUserId,
            },
        };
    }

    let uploadDocumentTask: FirebaseStorageTypes.Task;

    if (Platform.OS !== "web") {
        uploadDocumentTask = fileRef.putFile(sourceUri, metadata);
    } else {
        const base64 = sourceUri.split("base64,")[1];
        uploadDocumentTask = fileRef.put(Buffer.from(base64, "base64"), metadata);
    }
    uploadDocumentSignal.addEventListener("abort", () => {
        void uploadDocumentTask.cancel();
    });

    await uploadDocumentTask;

    return await fileRef.getDownloadURL();
};
